import { PER_PAGE } from "./publicArtApi";

const SKETCHFAB_MODEL_URL = "https://sketchfab.com/models/";

let apiKey = ""; // Private variable to store the Sketchfab API key
let categories = [];

export const PUBLIC_ASSETS_MUSEUMS = {
  sketchfab: {
    id: "sketchfab",
    name: "Sketchfab"
  }
};

/**
 * Sets the Sketchfab API key.
 * @param {String} key - The Sketchfab API key.
 */
export function setApiKey(key) {
  apiKey = key;
}

/**
 * Fetches categories from Sketchfab API.
 * @param {AbortSignal} [signal] - An optional AbortSignal to cancel the request.
 * @returns {Promise<Array>} - A promise that resolves with an array of categories.
 */
export async function fetchCategories(signal) {
  try {
    if (categories.length) {
      return categories;
    }

    const response = await fetch(`https://api.sketchfab.com/v3/categories?&key=${apiKey}`, { signal });
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data.results;
  } catch (error) {
    if (error?.name !== "AbortError") {
      console.error("There was a problem fetching categories:", error);
    }
    throw error; // Rethrow to handle it in the component
  }
}

/**
 * Fetches assets/models from Sketchfab API based on the selected category.
 * @param {String} category - The selected category for which to fetch assets.
 * @param {AbortSignal} [signal] - An optional AbortSignal to cancel the request.
 * @returns {Promise<Array>} - A promise that resolves with an array of assets.
 */
export async function fetchAssets(category = "", cursor = "", query = "", signal) {
  try {
    categories = await fetchCategories(signal); // Ensure categories are fetched before fetching assets

    let url = `https://api.sketchfab.com/v3/search?key=${apiKey}&type=models&count=${PER_PAGE}&sort_by=-likeCount&downloadable=true&staffpicked=true`;
    // &viewable=true
    // Add a max-size of 20 mb to the query
    url += "&archives_max_size=20971520";

    if (cursor) {
      url += `&cursor=${cursor}`;
    }

    if (category) {
      url += `&categories=${category}`;
    }

    if (query) {
      url += `&q=${query}`;
    }

    const response = await fetch(url, { signal });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();

    // eslint-disable-next-line no-use-before-define
    const result = formatResponse({
      ...data,
      filterItems: categories
    });

    return ["", result];
  } catch (error) {
    if (error?.name !== "AbortError") {
      console.error("There was a problem fetching assets:", error);
    }
    throw error; // Rethrow to handle it in the component
  }
}

const formatEntries = artwork => {
  let urlPreview = "";

  if (artwork.primaryImageSmall) {
    urlPreview = artwork.primaryImageSmall;
  }

  if (artwork.thumbnails && artwork.thumbnails.images?.length) {
    const image = artwork.thumbnails.images.find(img => img.width >= 512) ?? artwork.thumbnails.images.slice(-1);

    urlPreview = image.url;
  }

  const creator = artwork.artistDisplayName ?? artwork.user?.displayName ?? "";

  const formatCollection = collection => {
    return categories.find(category => category.slug === collection)?.name ?? collection;
  };

  const collection = artwork.department ?? formatCollection(artwork.categories[0]?.name) ?? artwork.museum;

  return {
    ...artwork,
    attributions: {
      creator: {
        name: creator
      }
    },
    collection,
    id: artwork.id ?? artwork.uid,
    images: {
      preview: {
        url: urlPreview
      }
    },
    public_thumbnail: urlPreview,
    thumbnail: urlPreview,
    isCommunityMedia: true,
    title: artwork.title ?? artwork.name,
    name: artwork.title ?? artwork.name,
    type: "model",
    artwork: {
      artist: creator,
      title: artwork.title ?? artwork.name,
      museum: artwork.museum,
      artwork_model: SKETCHFAB_MODEL_URL + artwork.uid,
      category: collection
    },
    url: "https://sketchfab.com/models/" + artwork.uid,
    source: "sketchfab"
  };
};

const formatResponse = ({ results: artworks, cursors, filterItems }) => {
  const entries = artworks.map(artwork => {
    return formatEntries({
      ...artwork,
      museum: "Sketchfab"
    });
  });

  // When the cursor is 12, the prev comes as null,
  // since the prev cursor is the first page.
  // We need to set it to 0, because null make the previous button disabled
  const prev_cursor = cursors.next > PER_PAGE && !cursors.previous ? 0 : cursors.previous;

  const meta = {
    next_cursor: cursors.next,
    prev_cursor,
    pagination: {
      cursors
    }
  };

  return { entries, meta, filterItems };
};
